/**
 * The help articles' Markdown, rendered as Solid elements (2026-10-05).
 *
 * Deliberately small: ## and ### headings, paragraphs, bullet and numbered
 * lists, **bold**, `code` and links - the subset OPApps' help site agreed to
 * render too (CONTRACTS/op-report.md section 4), so an article looks the same
 * in both places. No raw HTML and nothing goes through innerHTML: every piece
 * of text becomes a text node, so an article can never inject markup.
 *
 * A link written `[words](help:other-slug)` opens that answer in the app's own
 * help view; an http(s) link opens a new tab; anything else is shown as text.
 */

import { For, Switch, Match, type JSX } from 'solid-js';

type Inline = { t: 'text'; v: string } | { t: 'bold'; v: string } | { t: 'code'; v: string } | { t: 'link'; v: string; href: string };
type Block =
  | { t: 'h2' | 'h3' | 'p'; inl: Inline[] }
  | { t: 'ul' | 'ol'; items: Inline[][]; start: number };

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  const re = /\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ t: 'text', v: text.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ t: 'bold', v: m[1] });
    else if (m[2] !== undefined) out.push({ t: 'code', v: m[2] });
    else out.push({ t: 'link', v: m[3]!, href: m[4]! });
    last = re.lastIndex;
  }
  if (last < text.length) out.push({ t: 'text', v: text.slice(last) });
  return out;
}

export function parseBlocks(md: string): Block[] {
  const blocks: Block[] = [];
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  let para: string[] = [];
  let list: { t: 'ul' | 'ol'; items: string[]; start: number } | null = null;
  const flushPara = () => { if (para.length) { blocks.push({ t: 'p', inl: parseInline(para.join(' ')) }); para = []; } };
  const flushList = () => { if (list) { blocks.push({ t: list.t, items: list.items.map(parseInline), start: list.start }); list = null; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = /^(#{2,3})\s+(.*)$/.exec(line);
    const ul = /^\s*[-*]\s+(.*)$/.exec(line);
    const ol = /^\s*(\d+)[.)]\s+(.*)$/.exec(line);
    if (h) { flushPara(); flushList(); blocks.push({ t: h[1]!.length === 2 ? 'h2' : 'h3', inl: parseInline(h[2]!) }); }
    else if (ul || ol) {
      flushPara();
      const kind = ul ? 'ul' : 'ol';
      // A numbered list keeps the number it starts at (CommonMark), so steps
      // that resume after a paragraph do not restart at 1.
      if (!list || list.t !== kind) { flushList(); list = { t: kind, items: [], start: ol ? Number(ol[1]) : 1 }; }
      list.items.push(ul ? ul[1]! : ol![2]!);
    } else if (line.trim() === '') { flushPara(); flushList(); }
    else if (list && /^\s{2,}\S/.test(raw)) { list.items[list.items.length - 1] += ` ${line.trim()}`; }
    else { flushList(); para.push(line.trim()); }
  }
  flushPara();
  flushList();
  return blocks;
}

function Inlines(props: { inl: Inline[]; onHelpLink: (slug: string) => void }): JSX.Element {
  return (
    <For each={props.inl}>
      {(n) => (
        <Switch fallback={<>{n.v}</>}>
          <Match when={n.t === 'bold'}><strong>{n.v}</strong></Match>
          <Match when={n.t === 'code'}><code class="help-code">{n.v}</code></Match>
          <Match when={n.t === 'link' && (n as { href: string }).href.startsWith('help:')}>
            <button type="button" class="help-link" onClick={() => props.onHelpLink((n as { href: string }).href.slice(5))}>{n.v}</button>
          </Match>
          <Match when={n.t === 'link' && /^https?:\/\//.test((n as { href: string }).href)}>
            <a class="help-link" href={(n as { href: string }).href} target="_blank" rel="noopener noreferrer">{n.v}</a>
          </Match>
        </Switch>
      )}
    </For>
  );
}

export function Markdown(props: { source: string; onHelpLink: (slug: string) => void }): JSX.Element {
  const blocks = () => parseBlocks(props.source);
  return (
    <div class="help-md">
      <For each={blocks()}>
        {(b) => (
          <Switch>
            <Match when={b.t === 'h2'}><h3 class="help-h2"><Inlines inl={(b as { inl: Inline[] }).inl} onHelpLink={props.onHelpLink} /></h3></Match>
            <Match when={b.t === 'h3'}><h4 class="help-h3"><Inlines inl={(b as { inl: Inline[] }).inl} onHelpLink={props.onHelpLink} /></h4></Match>
            <Match when={b.t === 'p'}><p class="help-p"><Inlines inl={(b as { inl: Inline[] }).inl} onHelpLink={props.onHelpLink} /></p></Match>
            <Match when={b.t === 'ul'}>
              <ul class="help-ul"><For each={(b as { items: Inline[][] }).items}>{(it) => <li><Inlines inl={it} onHelpLink={props.onHelpLink} /></li>}</For></ul>
            </Match>
            <Match when={b.t === 'ol'}>
              <ol class="help-ol" start={(b as { start: number }).start}><For each={(b as { items: Inline[][] }).items}>{(it) => <li><Inlines inl={it} onHelpLink={props.onHelpLink} /></li>}</For></ol>
            </Match>
          </Switch>
        )}
      </For>
    </div>
  );
}
